import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import { apiFetch } from "@contractor-quoting/api-client";
import { Button, Card } from "@contractor-quoting/shared-ui";
import { CustomerWorkspaceShell } from "../components/CustomerWorkspaceShell.js";
import { SaveToast, useSaveToast } from "../components/SaveToast.js";
import { safeReturnTo } from "../return-to.js";

export function CustomerContractorsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<
    Array<{
      slug: string;
      name: string;
      description: string;
      serviceCities: Array<{ city: string; region: string }>;
    }>
  >([]);
  const [city, setCity] = useState("");
  const [defaultCity, setDefaultCity] = useState("");
  const [error, setError] = useState("");
  const search = (value: string) =>
    apiFetch<{ items: typeof items }>(
      `/v1/public/contractors${value ? `?city=${encodeURIComponent(value)}` : ""}`,
    )
      .then((result) => setItems(result.items))
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Search failed"),
      );

  useEffect(() => {
    void apiFetch<{ addresses: Array<{ city: string }> }>("/v1/customer/profile")
      .then((profile) => {
        const next = profile.addresses[0]?.city ?? "";
        setCity(next);
        setDefaultCity(next);
        return search(next);
      })
      .catch(() => search(""));
  }, []);

  return (
    <CustomerWorkspaceShell>
      <main className="h-full overflow-y-auto p-5 sm:p-8">
        <div className="mx-auto max-w-5xl">
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
            Contractor marketplace
          </p>
          <h1 className="mt-2 text-4xl font-black">Find a contractor</h1>
          <p className="mt-2 text-stone-500">
            Results default to your city. Clear the city to browse anywhere.
          </p>
          <div className="mt-6 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-stone-400" size={19} />
              <input
                className="w-full rounded-xl border border-stone-300 bg-white py-2.5 pl-10 pr-10"
                placeholder="City"
                value={city}
                onChange={(event) => setCity(event.target.value)}
              />
              {city ? (
                <button
                  aria-label="Clear city"
                  className="absolute right-3 top-3"
                  onClick={() => {
                    setCity("");
                    void search("");
                  }}
                >
                  <X size={18} />
                </button>
              ) : null}
            </div>
            <Button onClick={() => void search(city)}>Search</Button>
          </div>
          {defaultCity && city !== defaultCity ? (
            <button
              className="mt-2 text-sm font-bold text-emerald-800"
              onClick={() => {
                setCity(defaultCity);
                void search(defaultCity);
              }}
            >
              Use {defaultCity}
            </button>
          ) : null}
          {error ? <p className="mt-4 text-red-700">{error}</p> : null}
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {items.map((contractor) => (
              <Card key={contractor.slug}>
                <h2 className="text-xl font-black">{contractor.name}</h2>
                <p className="mt-2 text-stone-600">
                  {contractor.description || "Local contractor on Mezoniq"}
                </p>
                <p className="mt-3 text-sm text-stone-500">
                  {contractor.serviceCities
                    .map((place) => `${place.city}, ${place.region}`)
                    .join(" · ") || "Service locations coming soon"}
                </p>
                <Button className="mt-5" onClick={() => void navigate(`/chat/${contractor.slug}`)}>
                  Start a project
                </Button>
              </Card>
            ))}
          </div>
          {items.length === 0 ? (
            <Card className="mt-6 text-center">
              <p>No contractors match this city. Clear the city to search everywhere.</p>
            </Card>
          ) : null}
        </div>
      </main>
    </CustomerWorkspaceShell>
  );
}

export function CustomerProfilePage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const returnTo = safeReturnTo(search.get("returnTo"), "");
  const [profile, setProfile] = useState({
    displayName: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    region: "ON",
    postalCode: "",
    country: "CA",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [returnPending, setReturnPending] = useState(false);
  const toast = useSaveToast();

  useEffect(() => {
    interface ProfileResponse {
      display_name: string | null;
      phone: string | null;
      addresses: Array<{
        line1: string;
        line2: string | null;
        city: string;
        region: string;
        postal_code: string;
        country: string;
      }>;
    }
    void apiFetch<ProfileResponse>("/v1/customer/profile").then((data) => {
      const address = data.addresses[0];
      setProfile({
        displayName: data.display_name ?? "",
        phone: data.phone ?? "",
        line1: address?.line1 ?? "",
        line2: address?.line2 ?? "",
        city: address?.city ?? "",
        region: address?.region ?? "ON",
        postalCode: address?.postal_code ?? "",
        country: address?.country ?? "CA",
      });
    });
  }, []);

  useEffect(() => {
    if (returnPending && !toast.message && returnTo) void navigate(returnTo, { replace: true });
  }, [navigate, returnPending, returnTo, toast.message]);

  const save = async () => {
    setError("");
    setReturnPending(false);
    toast.clear();
    setSaving(true);
    try {
      await apiFetch("/v1/customer/profile", {
        method: "PATCH",
        body: JSON.stringify({
          displayName: profile.displayName || null,
          phone: profile.phone || null,
        }),
      });
      await apiFetch("/v1/customer/addresses/default", {
        method: "PUT",
        body: JSON.stringify({ label: "Home", ...profile }),
      });
      toast.show("Profile saved");
      setReturnPending(Boolean(returnTo));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Profile could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomerWorkspaceShell>
      <SaveToast message={toast.message} />
      <main className="h-full overflow-y-auto p-5 sm:p-8">
        <div className="mx-auto max-w-2xl">
          <h1 className="text-4xl font-black">My profile</h1>
          <Card className="mt-6">
            <div className="grid gap-4 sm:grid-cols-2">
              {(
                [
                  ["displayName", "Name"],
                  ["phone", "Phone"],
                  ["line1", "Address line 1"],
                  ["line2", "Address line 2"],
                  ["city", "City"],
                  ["region", "Province / region"],
                  ["postalCode", "Postal code"],
                  ["country", "Country"],
                ] as const
              ).map(([key, label]) => (
                <label
                  key={key}
                  className={key === "line1" || key === "line2" ? "sm:col-span-2" : ""}
                >
                  <span className="text-sm font-bold">{label}</span>
                  <input
                    className="mt-1 w-full rounded-xl border border-stone-300 p-3"
                    value={profile[key]}
                    onChange={(event) => {
                      setError("");
                      setReturnPending(false);
                      toast.clear();
                      setProfile((current) => ({ ...current, [key]: event.target.value }));
                    }}
                  />
                </label>
              ))}
            </div>
            <Button className="mt-6" disabled={saving} onClick={() => void save()}>
              {saving ? "Saving…" : "Save profile"}
            </Button>
            {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
            {returnPending ? (
              <p className="mt-3 text-sm text-stone-500">Returning to your project…</p>
            ) : null}
          </Card>
        </div>
      </main>
    </CustomerWorkspaceShell>
  );
}
